import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/services/api";
import { Button } from "@/components/ui/button";
import {
  Bell,
  Check,
  CheckCheck,
  Trash2,
  Package,
  CreditCard,
  UserPlus,
  ClipboardList,
  DollarSign,
  ShoppingCart,
  Truck,
  PackageCheck,
  AlertTriangle,
  X,
} from "lucide-react";

// Configuración de íconos y colores por tipo
const tipoConfig = {
  // Admin
  pedido_catalogo_nuevo: { icon: ShoppingCart, color: "text-blue-600", bg: "bg-blue-50" },
  transferencia_pendiente: { icon: CreditCard, color: "text-purple-600", bg: "bg-purple-50" },
  solicitud_vendedor: { icon: UserPlus, color: "text-green-600", bg: "bg-green-50" },
  requisicion_nueva: { icon: ClipboardList, color: "text-orange-600", bg: "bg-orange-50" },
  comision_pendiente: { icon: DollarSign, color: "text-yellow-600", bg: "bg-yellow-50" },
  pedido_online_nuevo: { icon: Package, color: "text-indigo-600", bg: "bg-indigo-50" },
  // Vendedor
  pedido_recibido: { icon: ShoppingCart, color: "text-green-600", bg: "bg-green-50" },
  pedido_listo: { icon: PackageCheck, color: "text-purple-600", bg: "bg-purple-50" },
  pedido_entregado: { icon: Truck, color: "text-blue-600", bg: "bg-blue-50" },
  pago_confirmado: { icon: Check, color: "text-green-600", bg: "bg-green-50" },
  stock_bajo: { icon: AlertTriangle, color: "text-red-600", bg: "bg-red-50" },
  comision_generada: { icon: DollarSign, color: "text-green-600", bg: "bg-green-50" },
};

const prioridadConfig = {
  alta: "border-l-4 border-l-red-500",
  media: "border-l-4 border-l-yellow-500",
  baja: "border-l-4 border-l-gray-300",
};

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const navigate = useNavigate();

  // Fetch count and notifications
  const fetchNotifications = useCallback(async () => {
    try {
      const [countRes, listRes] = await Promise.all([
        api.get("/notificaciones/count"),
        api.get("/notificaciones?limit=20")
      ]);
      setUnreadCount(countRes.data.count);
      setNotifications(listRes.data);
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  }, []);

  // Initial fetch and polling
  useEffect(() => {
    fetchNotifications();
    // Poll every 30 seconds
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleToggle = async () => {
    if (!open) {
      setLoading(true);
      await fetchNotifications();
      setLoading(false);
      
      // Calculate optimal position for the dropdown
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const viewportWidth = window.innerWidth;
        const dropdownWidth = 384; // w-96
        const dropdownHeight = Math.min(viewportHeight * 0.7, 500);
        
        let top, left;
        
        // Vertical positioning: prefer below, but go above if not enough space
        if (rect.bottom + dropdownHeight + 20 > viewportHeight) {
          // Open upward
          top = rect.top - dropdownHeight - 8;
        } else {
          // Open downward
          top = rect.bottom + 8;
        }
        
        // Horizontal positioning: ensure it stays within viewport
        // If button is on the left side (sidebar), open to the right
        if (rect.left < 300) {
          // Button is in sidebar, open to the right of the sidebar
          left = Math.min(rect.left, viewportWidth - dropdownWidth - 16);
        } else {
          // Button is on the right, align right edge with button
          left = Math.max(16, rect.right - dropdownWidth);
        }
        
        // Ensure minimum left position
        left = Math.max(16, left);
        // Ensure it doesn't overflow right
        if (left + dropdownWidth > viewportWidth - 16) {
          left = viewportWidth - dropdownWidth - 16;
        }
        
        setDropdownPosition({ top, left });
      }
    }
    setOpen(!open);
  };

  const handleMarkAsRead = async (notifId) => {
    try {
      await api.put(`/notificaciones/${notifId}/leer`);
      setNotifications(prev => 
        prev.map(n => n.id === notifId ? { ...n, leida: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error("Error marking as read:", error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await api.put("/notificaciones/leer-todas");
      setNotifications(prev => prev.map(n => ({ ...n, leida: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error("Error marking all as read:", error);
    }
  };

  const handleDelete = async (notifId) => {
    try {
      await api.delete(`/notificaciones/${notifId}`);
      setNotifications(prev => prev.filter(n => n.id !== notifId));
    } catch (error) {
      console.error("Error deleting notification:", error);
    }
  };

  const handleClick = async (notif) => {
    if (!notif.leida) {
      await handleMarkAsRead(notif.id);
    }
    if (notif.link) {
      navigate(notif.link);
      setOpen(false);
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return "Ahora";
    if (diffMins < 60) return `${diffMins}m`;
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays < 7) return `${diffDays}d`;
    return date.toLocaleDateString("es-CO", { day: "numeric", month: "short" });
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <button
        ref={buttonRef}
        onClick={handleToggle}
        className="relative p-2 rounded-full hover:bg-torta-cream/50 transition-colors"
        data-testid="notification-bell"
      >
        <Bell className={`w-5 h-5 ${unreadCount > 0 ? "text-torta-brown" : "text-torta-muted"}`} />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-medium animate-pulse">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown - Fixed positioning for consistent placement */}
      {open && (
        <div 
          className="fixed w-96 bg-white rounded-xl shadow-xl border border-torta-sand/50 z-[100] max-h-[70vh] flex flex-col"
          style={{
            top: dropdownPosition.top,
            left: dropdownPosition.left,
            maxWidth: 'calc(100vw - 2rem)',
          }}
        >
          {/* Header */}
          <div className="p-3 border-b border-torta-sand/30 flex items-center justify-between">
            <h3 className="font-display text-torta-dark font-medium">Notificaciones</h3>
            <div className="flex items-center gap-1">
              {unreadCount > 0 && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={handleMarkAllAsRead}
                  className="text-xs text-torta-muted hover:text-torta-brown"
                >
                  <CheckCheck className="w-4 h-4 mr-1" />
                  Marcar todas
                </Button>
              )}
              <button onClick={() => setOpen(false)} className="p-1 hover:bg-torta-cream rounded">
                <X className="w-4 h-4 text-torta-muted" />
              </button>
            </div>
          </div>

          {/* List */}
          <div className="overflow-y-auto flex-1">
            {loading ? (
              <div className="p-8 text-center">
                <div className="animate-spin w-6 h-6 border-2 border-torta-brown border-t-transparent rounded-full mx-auto"></div>
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-8 text-center">
                <Bell className="w-10 h-10 text-torta-sand mx-auto mb-2" />
                <p className="text-torta-muted text-sm">No hay notificaciones</p>
              </div>
            ) : (
              <div className="divide-y divide-torta-sand/20">
                {notifications.map((notif) => {
                  const config = tipoConfig[notif.tipo] || { icon: Bell, color: "text-gray-600", bg: "bg-gray-50" };
                  const Icon = config.icon;
                  const prioridadClass = prioridadConfig[notif.prioridad] || "";

                  return (
                    <div
                      key={notif.id}
                      className={`p-3 hover:bg-torta-cream/30 cursor-pointer transition-colors ${
                        !notif.leida ? "bg-torta-cream/20" : ""
                      } ${prioridadClass}`}
                      onClick={() => handleClick(notif)}
                    >
                      <div className="flex gap-3">
                        <div className={`w-9 h-9 rounded-full ${config.bg} flex items-center justify-center flex-shrink-0`}>
                          <Icon className={`w-4 h-4 ${config.color}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <p className={`text-sm ${!notif.leida ? "font-medium text-torta-dark" : "text-torta-muted"}`}>
                              {notif.titulo}
                            </p>
                            <span className="text-xs text-torta-muted flex-shrink-0">
                              {formatTime(notif.fecha_creacion)}
                            </span>
                          </div>
                          <p className="text-xs text-torta-muted mt-0.5 line-clamp-2">{notif.mensaje}</p>
                        </div>
                        <div className="flex flex-col gap-1">
                          {!notif.leida && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleMarkAsRead(notif.id);
                              }}
                              className="p-1 hover:bg-torta-cream rounded"
                              title="Marcar como leída"
                            >
                              <Check className="w-3 h-3 text-torta-muted" />
                            </button>
                          )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(notif.id);
                            }}
                            className="p-1 hover:bg-red-50 rounded"
                            title="Eliminar"
                          >
                            <Trash2 className="w-3 h-3 text-red-400" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
